"use strict";

var _ = require("lodash");

module.exports = /* @ngInject */ function(
  $q,
  $log,
  OpenfinWindowFactory,
  workspaceService,
  Workspace,
  menuItemRepository,
  RediWindowRepository,
  RediWindowService,
  RediPlusWindowRepository,
  sideBySideApi,
  sideBySideSessionRepository,
  sideBySideInitializer,
  eikonUsageCheck,
  openFinApi
) {
  var that = this;

  that.open = _.debounce(open, 5000, { leading: true, trailing: false });

  function open(workspaceName, workspaceOwner) {
    that
      .close()
      .then(function() {
        return OpenfinWindowFactory.clearCache();
      })
      .then(function() {
        openWorkspaceWindows(workspaceName, workspaceOwner);
      });
  }

  function openWorkspaceWindows(workspaceName, workspaceOwner) {
    workspaceService
      .getOrCreateWorkspaceByName(workspaceName, workspaceOwner)
      .then(function(workspace) {
        openOpenfinWindows(workspace);
        openEikonWorkspaceWindows(workspace).finally(function() {
          workspaceService.setLocalWorkspace(
            Workspace.prototype.serverModel.apply(workspace)
          );
        });
      });
  }

  function openOpenfinWindows(workspace) {
    _.forEach(workspace.getAllWindows(), function(workspaceWindow) {
      menuItemRepository
        .getByType(workspaceWindow.getName())
        .then(function(menuItem) {
          RediWindowService.createRediWindowFromWorkspace(
            menuItem,
            workspace,
            workspaceWindow.getId()
          );
        });
    });
  }

  function openEikonWorkspaceWindows(workspace) {
    return eikonUsageCheck
      .isUsingEikonLight()
      .then(function(isUsingEikonLight) {
        if (
          isUsingEikonLight &&
          sideBySideSessionRepository.hasValidSession()
        ) {
          var existingEikonInstanceId = workspace.getEikonInstanceId();
          if (existingEikonInstanceId) {
            RediPlusWindowRepository.setWorkspaceWindows(
              workspace.getRediPlusWindows()
            );
            sideBySideInitializer
              .initExistingInstance(existingEikonInstanceId)
              .then(function() {
                sideBySideApi.loadEikonWorkspace(
                  workspace.getEikonWorkspaceData()
                );
              });
          } else {
            $log.info("No Eikon instance to load.");
          }
        }
      });
  }

  that.close = function() {
    return closeEikonWindows()
      .then(closeOpenFinWindows, closeOpenFinWindows)
      .finally(cleanupWorkspaceWindows);
  };

  function closeEikonWindows() {
    return eikonUsageCheck
      .isUsingEikonLight()
      .then(function(isUsingEikonLight) {
        if (
          isUsingEikonLight &&
          sideBySideSessionRepository.hasValidSession()
        ) {
          return sideBySideApi.closeEikonWorkspace();
        }
      });
  }

  function closeOpenFinWindows() {
    return OpenfinWindowFactory.executeCloseAllWindows();
  }

  function cleanupWorkspaceWindows() {
    RediWindowRepository.clearAllWindows();
    workspaceService.clearWorkspaceStoredLocally();
  }

  function getActiveOpenfinWindows(currentActiveWindows) {
    return $q
      .all(_.map(currentActiveWindows, mapToCurrentActiveOpenfinWindows))
      .then(filterNonOpenfinWindows);
  }

  function mapToCurrentActiveOpenfinWindows(rediWindow) {
    return rediWindow.isOpenfinContainer().then(function(isOpenfinContainer) {
      if (isOpenfinContainer) {
        return rediWindow;
      } else {
        return undefined;
      }
    });
  }

  function filterNonOpenfinWindows(windows) {
    return _.filter(windows, function(window) {
      return !!window;
    });
  }

  /**
   * saveWorkspaceWithoutWindows() was removed as part of REDIWEBC-5544.
   * While testing it was observed that workspace was emptied first and then
   * the current workspace was updated.
   * The empty workspace object is only needed to populate the current windows.
   * Hence removed the empty db call and replaced it with a empty workspace
   * object which can be updated with new windows and saved later.
   */
  that.save = function(workspaceName) {
    return workspaceService
      .getOrCreateWorkspaceByName(workspaceName)
      .then(
        workspaceFromServer =>
          new Workspace({
            id: workspaceFromServer.getId(),
            name: workspaceName,
            rediPlusWindows: null,
            windows: [],
            eikonWorkspace: null
          })
      )
      .then(addOpenFinWindowsToWorkspace)
      .then(addEikonWorkspaceToWorkspace)
      .then(addRediPlusWindowsToWorkspace)
      .then(saveWorkspace)
      .catch(function(error) {
        const errorMsg = `${workspaceName} workspace save failed: `;
        $log.error(errorMsg, error);
        openFinApi.publish(
          "save-workspace-failure",
          `${errorMsg} ${error && (error.data || error.message)}`
        );
      });
  };

  function addOpenFinWindowsToWorkspace(workspace) {
    var currentActiveWindows = RediWindowRepository.getAllWindows();
    return getActiveOpenfinWindows(currentActiveWindows)
      .then(
        function(activeOpenfinWindows) {
          return $q.all(
            _.map(activeOpenfinWindows, function(rediWindow) {
              return addOpenFinWindowToWorkspace(workspace, rediWindow);
            })
          );
        },
        function(error) {
          return $q.reject(
            error ||
              new Error(
                "An unexpected error occurred while getting Active Openfin Windows"
              )
          );
        }
      )
      .then(function() {
        return workspace;
      });
  }

  function addOpenFinWindowToWorkspace(workspace, rediWindow) {
    return rediWindow.getLayout().then(function(layout) {
      return workspaceService
        .getOrCreateWorkspace()
        .then(function(localWorkspace) {
          return getOrCreateWindow(localWorkspace, rediWindow, layout);
        })
        .then(function(localWorkspaceWindow) {
          var workspaceWindow = getOrCreateWindow(
            workspace,
            rediWindow,
            layout
          );
          workspaceWindow.setLayout(layout);
          workspaceWindow.setData(localWorkspaceWindow.getData());
          workspace.setWindow(workspaceWindow);
          return workspace;
        });
    });
  }

  function getOrCreateWindow(workspace, rediWindow, layout) {
    return workspace.getOrCreateWindow(
      rediWindow.getType(),
      rediWindow.getName(),
      layout,
      workspaceService.getDefaultData()
    );
  }

  function addEikonWorkspaceToWorkspace(workspace) {
    return eikonUsageCheck
      .isUsingEikonLight()
      .then(function(isUsingEikonLight) {
        if (
          isUsingEikonLight &&
          sideBySideSessionRepository.hasValidSession()
        ) {
          return sideBySideApi.getEikonWorkspace().then(
            function(eikonWorkspace) {
              workspace.setEikonInstanceId(
                sideBySideSessionRepository.getInstanceId()
              );
              workspace.setEikonWorkspaceData(
                JSON.parse(eikonWorkspace.content)
              );
              return workspace;
            },
            function(error) {
              return $q.reject(
                error ||
                  new Error(
                    "An unexpected error occurred while adding Eikon Workspace windows to workspace"
                  )
              );
            }
          );
        } else {
          return workspace;
        }
      });
  }

  function addRediPlusWindowsToWorkspace(workspace) {
    return eikonUsageCheck
      .isUsingEikonLight()
      .then(function(isUsingEikonLight) {
        if (
          isUsingEikonLight &&
          sideBySideSessionRepository.hasValidSession()
        ) {
          var rediPlusWindows = RediPlusWindowRepository.getAllWindows();
          workspace.setRediPlusWindows(rediPlusWindows);
        }
        return workspace;
      });
  }

  function saveWorkspace(workspace) {
    workspaceService.setLocalWorkspace(workspace.serverModel());
    return workspaceService.saveExistingWorkspace(workspace).then(
      function() {
        openFinApi.publish("save-workspace-success");
      },
      function(error) {
        return $q.reject(
          error ||
            new Error(
              "An unexpected error occurred while saving existing workspace"
            )
        );
      }
    );
  }
};



// WEBPACK FOOTER //
// ./app/business/rediPlusWorkspaceService.js